import { useState, useEffect } from 'react';
import { getFrontendUrlBuilder } from '../utils/url-builder';
import axios from 'axios';

const urlBuilder = getFrontendUrlBuilder();
const API_URL = urlBuilder.getApiBaseUrl();

interface VerificationBannerProps {
  emailVerified: boolean;
}

export function VerificationBanner({ emailVerified }: VerificationBannerProps) {
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');
  const [retryAfter, setRetryAfter] = useState<number>(0);

  // Don't show banner if email is already verified
  if (emailVerified) {
    return null;
  }

  // Countdown timer for rate limiting
  useEffect(() => {
    if (retryAfter > 0) {
      const timer = setInterval(() => {
        setRetryAfter((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [retryAfter]);

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendStatus('idle');
    setResendMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/resend-verification`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setResendStatus('success');
        setResendMessage(response.data.message || 'Verification email sent!');

        // Set retry timer to 5 minutes (300 seconds)
        setRetryAfter(300);
      } else {
        setResendStatus('error');
        setResendMessage(response.data.message || 'Failed to resend email');
      }
    } catch (error: any) {
      setResendStatus('error');

      if (error.response?.status === 429) {
        // Rate limited
        const retryAfterSeconds = error.response.headers['retry-after'];
        if (retryAfterSeconds) {
          setRetryAfter(parseInt(retryAfterSeconds, 10));
        }
        setResendMessage(
          error.response.data?.message || 'Please wait before requesting another email'
        );
      } else {
        setResendMessage(
          error.response?.data?.message || 'An error occurred. Please try again.'
        );
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-1">
            <span className="flex p-2 rounded-lg bg-yellow-100">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </span>
            <p className="ml-3 text-sm font-medium text-yellow-800">
              Your email address is not verified. Please check your inbox for a verification email.
            </p>
          </div>

          <div className="mt-2 flex-shrink-0 w-full sm:mt-0 sm:w-auto">
            <div className="flex items-center space-x-2">
              {resendStatus === 'success' && (
                <span className="text-sm text-green-600 font-medium">
                  {resendMessage}
                </span>
              )}
              {resendStatus === 'error' && (
                <span className="text-sm text-red-600 font-medium">
                  {resendMessage}
                </span>
              )}
              <button
                onClick={handleResendEmail}
                disabled={isResending || retryAfter > 0}
                className={`
                  inline-flex items-center px-4 py-2 border border-transparent
                  text-sm font-medium rounded-md text-yellow-800 bg-yellow-100
                  hover:bg-yellow-200 focus:outline-none focus:ring-2
                  focus:ring-offset-2 focus:ring-yellow-500
                  ${(isResending || retryAfter > 0) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {isResending ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-800"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Sending...
                  </>
                ) : retryAfter > 0 ? (
                  `Resend (wait ${Math.floor(retryAfter / 60)}:${String(retryAfter % 60).padStart(2, '0')})`
                ) : (
                  'Resend Email'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
